import { DestroyRef, inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { AuthService, hasStaffRole } from '@nebalkon-workspace/auth';
import { RedirectQueueService } from '../common/services/redirect-queue.service';
import { isWarehouseUuid } from '../common/utils/warehouse-route.utils';

/** Основной префикс deep links списка заказов (staff). */
export const STUFF_ORDERS_DEEP_LINK_PREFIX = '/stuff/orders/';

/** Устаревший префикс (редиректится на orders). */
export const STUFF_STORAGE_REQUEST_DEEP_LINK_PREFIX = '/stuff/requests/';

/** Префикс пути для deep links карточки склада (staff). */
export const STUFF_WAREHOUSE_DEEP_LINK_PREFIX = '/stuff/warehouses/';

/** Путь deep link возврата после оплаты кошелька (client). */
export const CLIENT_WALLET_TOPUP_DEEP_LINK_PATH = '/client';

/** Custom scheme возврата после оплаты кошелька: `nebalkon://client?intent=...`. */
export const CLIENT_WALLET_TOPUP_DEEP_LINK_SCHEME = 'nebalkon';
export const CLIENT_WALLET_TOPUP_DEEP_LINK_HOST = 'client';

@Injectable({ providedIn: 'root' })
export class DeepLinkService {
  readonly #router = inject(Router);
  readonly #zone = inject(NgZone);
  readonly #destroyRef = inject(DestroyRef);
  readonly #auth = inject(AuthService);
  readonly #redirectQueue = inject(RedirectQueueService);

  /** Сглаживание повторной обработки того же URL (cold start: getLaunchUrl + appUrlOpen). */
  #lastHandledUrl: string | null = null;
  #lastHandledAt = 0;

  constructor() {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    void (async (): Promise<void> => {
      const handle = await App.addListener('appUrlOpen', (event) => {
        this.#handleUrlOnce(event.url);
      });
      this.#destroyRef.onDestroy(() => void handle.remove());

      try {
        const launch = await App.getLaunchUrl();
        if (launch?.url) {
          this.#handleUrlOnce(launch.url);
        }
      } catch {
        // getLaunchUrl не поддерживается или недоступен
      }
    })();
  }

  #handleUrlOnce(urlString: string): void {
    const now = Date.now();
    if (
      urlString === this.#lastHandledUrl &&
      now - this.#lastHandledAt < 1500
    ) {
      return;
    }
    this.#lastHandledUrl = urlString;
    this.#lastHandledAt = now;

    this.#zone.run(() => {
      this.#routeFromAppUrl(urlString);
    });
  }

  /** Парсинг pathname и search из полного URL или относительной строки. */
  #parsePathAndSearch(
    urlString: string,
  ): { pathname: string; search: string } | null {
    try {
      const u = new URL(urlString);
      if (
        u.protocol === `${CLIENT_WALLET_TOPUP_DEEP_LINK_SCHEME}:` &&
        u.hostname === CLIENT_WALLET_TOPUP_DEEP_LINK_HOST
      ) {
        return {
          pathname: CLIENT_WALLET_TOPUP_DEEP_LINK_PATH,
          search: u.search,
        };
      }
      return { pathname: u.pathname, search: u.search };
    } catch {
      try {
        const u = new URL(urlString, 'https://local.invalid');
        return { pathname: u.pathname, search: u.search };
      } catch {
        return null;
      }
    }
  }

  /** Детальная карточка заказа: `/stuff/orders/:id` (или устар. `/stuff/requests/:id`). */
  #parseStuffRequestDetailPath(pathname: string): string | null {
    const prefixes = [
      STUFF_ORDERS_DEEP_LINK_PREFIX,
      STUFF_STORAGE_REQUEST_DEEP_LINK_PREFIX,
    ] as const;
    for (const prefix of prefixes) {
      if (!pathname.startsWith(prefix)) {
        continue;
      }
      const rest = pathname.slice(prefix.length).replace(/\/$/, '');
      if (!rest || rest.includes('/')) {
        return null;
      }
      return `${STUFF_ORDERS_DEEP_LINK_PREFIX}${rest}`;
    }
    return null;
  }

  /**
   * Карточка склада: `/stuff/warehouses/:id` (UUID) или `/stuff/warehouses/slug/:slug`.
   * Входящий `/stuff/warehouses/{slug}` без UUID превращается во внутренний путь с сегментом `slug/`.
   */
  #parseStuffWarehouseDetailPath(pathname: string): string | null {
    const prefix = STUFF_WAREHOUSE_DEEP_LINK_PREFIX;
    if (!pathname.startsWith(prefix)) {
      return null;
    }
    const rest = pathname.slice(prefix.length).replace(/\/$/, '');
    if (!rest || rest === 'create') {
      return null;
    }

    const slugRoutePrefix = 'slug/';
    if (rest.startsWith(slugRoutePrefix)) {
      const slug = rest.slice(slugRoutePrefix.length);
      if (!slug || slug.includes('/')) {
        return null;
      }
      return `${prefix}${slugRoutePrefix}${slug}`;
    }

    if (rest.includes('/')) {
      return null;
    }

    if (isWarehouseUuid(rest)) {
      return `${prefix}${rest}`;
    }

    return `${prefix}slug/${rest}`;
  }

  /**
   * Возврат после оплаты кошелька: `/client?intent=...` (опционально `failed=1`).
   */
  #parseClientWalletTopUpReturnPath(
    pathname: string,
    search: string,
  ): string | null {
    const normalized = pathname.replace(/\/$/, '') || '/';
    if (normalized !== CLIENT_WALLET_TOPUP_DEEP_LINK_PATH) {
      return null;
    }

    const params = new URLSearchParams(
      search.startsWith('?') ? search.slice(1) : search,
    );
    const intent = params.get('intent')?.trim();
    if (!intent) {
      return null;
    }

    return `${CLIENT_WALLET_TOPUP_DEEP_LINK_PATH}${search}`;
  }

  /** Staff deep links: same role source as guards ({@link AuthService.roles}). */
  #userHasStaffAccess(): boolean {
    return hasStaffRole(this.#auth.roles());
  }

  #closePaymentBrowser(): void {
    void Browser.close().catch(() => {
      // Custom Tab / SFSafariViewController may already be closed.
    });
  }

  #routeFromAppUrl(urlString: string): void {
    const parsed = this.#parsePathAndSearch(urlString);
    if (!parsed) {
      return;
    }

    const walletReturnPath = this.#parseClientWalletTopUpReturnPath(
      parsed.pathname,
      parsed.search,
    );
    if (walletReturnPath) {
      void this.#router.navigateByUrl(walletReturnPath);
      this.#closePaymentBrowser();
      return;
    }

    const detailPath =
      this.#parseStuffRequestDetailPath(parsed.pathname) ??
      this.#parseStuffWarehouseDetailPath(parsed.pathname);
    if (!detailPath) {
      return;
    }

    const targetPath = `${detailPath}${parsed.search}`;

    if (!this.#auth.isAuthenticated()) {
      this.#redirectQueue.add({
        url: targetPath,
        action: 'auth',
      });
      void this.#router.navigate(['/auth']);
      return;
    }

    if (!this.#userHasStaffAccess()) {
      this.#redirectQueue.add({
        url: targetPath,
        action: 'staff_deep_link',
      });
      void this.#router.navigate(['/client']);
      return;
    }

    void this.#router.navigateByUrl(targetPath);
  }
}
